// Searchable picker for reference codes that live in a table too large to load up
// front (job 27: TNI, MLF, DLF). Typing queries the table; selecting reports the
// code so the caller can fill in the dependent value.
//
// A code already saved on the record is always shown, even before any search runs
// and even if it is no longer in the table — the form must never appear to have
// silently dropped a value the user can see in the database.

import { useState, useEffect, useRef } from 'react';
import { ChevronsUpDown, Check, Loader2 } from 'lucide-react';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../../../../foundation/altzero_base/base/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../../../../foundation/altzero_base/base/components/ui/command';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';

type Props = {
  value: string;
  onSelect: (code: string) => void;
  /** Resolves the options for what has been typed so far. */
  search: (term: string) => Promise<string[]>;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
};

export function CodeCombobox({
  value, onSelect, search,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No matches',
  disabled,
  className,
}: Props) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Only the newest query may write state — a slower earlier one resolving late
  // would otherwise replace results for what the user has since typed.
  const requestRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    setError(null);
    const timer = setTimeout(() => {
      search(term)
        .then((codes) => {
          if (requestId !== requestRef.current) return;
          setOptions(codes);
        })
        .catch((err: unknown) => {
          if (requestId !== requestRef.current) return;
          setOptions([]);
          setError(err instanceof Error ? err.message : 'Lookup failed');
        })
        .finally(() => {
          if (requestId === requestRef.current) setLoading(false);
        });
    }, 250);
    return () => clearTimeout(timer);
  }, [open, term, search]);

  // Keep the saved code visible in the list even when the search doesn't return it.
  const shown = value && !options.includes(value) ? [value, ...options] : options;

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setTerm(''); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn('w-full justify-between font-mono font-normal', !value && 'text-muted-foreground', className)}
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={term}
            onValueChange={setTerm}
            className="h-9 text-xs"
          />
          <CommandList>
            {loading && (
              <div className="flex items-center gap-2 px-3 py-2.5 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
              </div>
            )}
            {error && <div className="px-3 py-2.5 text-xs text-red-600">{error}</div>}
            {!loading && !error && shown.length === 0 && <CommandEmpty>{emptyText}</CommandEmpty>}
            <CommandGroup>
              {shown.map((code) => (
                <CommandItem
                  key={code}
                  value={code}
                  onSelect={() => { onSelect(code); setOpen(false); setTerm(''); }}
                >
                  <Check className={cn('mr-2 h-3.5 w-3.5', value === code ? 'opacity-100' : 'opacity-0')} />
                  <span className="font-mono text-xs">{code}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
